import type { ExperimentConfig, ExperimentStep, ExperimentPart } from '@/types';

// ============================================================================
// EXPERIMENT 4: Molar Mass of Compounds
// ============================================================================

// Color codes for molar mass experiment
export const molarMassColors = {
  // UI Elements
  primaryBackground: '#F5F5F5',
  labBenchSurface: '#8B7355',
  balanceDisplay: '#1A1A1A',
  balanceNumbers: '#00FF00',
  volumetricFlask: '#E8F4F8',
  volumetricFlaskMark: '#0066CC',
  beaker: '#E0E8EC',
  deionizedWater: '#E6F3FF',
  weighBoat: '#FFFFFF',
  // Compound Colors
  nacl: '#FFFFFF',
  cuso4: '#4A90D9',
  caco3: '#FAFAFA',
  kno3: '#FFFFFF',
  naoh: '#F8F8F8',
  mgso4: '#FFFFFF',
  licl: '#FFFFFF',
  na2co3: '#FFFFFF',
  // Status Indicators
  correct: '#28A745',
  error: '#DC3545',
  inProgress: '#007BFF',
  neutral: '#6C757D',
};

// Compound data for the experiment
export interface CompoundData {
  id: string;
  name: string;
  formula: string;
  molarMass: number;
  color: string;
  elements: { symbol: string; atomicMass: number; count: number }[];
}

export const compoundLibrary: Record<string, CompoundData> = {
  nacl: {
    id: 'nacl',
    name: 'Sodium Chloride',
    formula: 'NaCl',
    molarMass: 58.44,
    color: '#FFFFFF',
    elements: [
      { symbol: 'Na', atomicMass: 22.99, count: 1 },
      { symbol: 'Cl', atomicMass: 35.45, count: 1 },
    ],
  },
  cuso4: {
    id: 'cuso4',
    name: 'Copper(II) Sulfate',
    formula: 'CuSO4',
    molarMass: 159.62,
    color: '#4A90D9',
    elements: [
      { symbol: 'Cu', atomicMass: 63.55, count: 1 },
      { symbol: 'S', atomicMass: 32.07, count: 1 },
      { symbol: 'O', atomicMass: 16.00, count: 4 },
    ],
  },
  caco3: {
    id: 'caco3',
    name: 'Calcium Carbonate',
    formula: 'CaCO3',
    molarMass: 100.09,
    color: '#FAFAFA',
    elements: [
      { symbol: 'Ca', atomicMass: 40.08, count: 1 },
      { symbol: 'C', atomicMass: 12.01, count: 1 },
      { symbol: 'O', atomicMass: 16.00, count: 3 },
    ],
  },
  kno3: {
    id: 'kno3',
    name: 'Potassium Nitrate',
    formula: 'KNO3',
    molarMass: 101.10,
    color: '#FFFFFF',
    elements: [
      { symbol: 'K', atomicMass: 39.10, count: 1 },
      { symbol: 'N', atomicMass: 14.01, count: 1 },
      { symbol: 'O', atomicMass: 16.00, count: 3 },
    ],
  },
  naoh: {
    id: 'naoh',
    name: 'Sodium Hydroxide',
    formula: 'NaOH',
    molarMass: 40.00,
    color: '#F8F8F8',
    elements: [
      { symbol: 'Na', atomicMass: 22.99, count: 1 },
      { symbol: 'O', atomicMass: 16.00, count: 1 },
      { symbol: 'H', atomicMass: 1.01, count: 1 },
    ],
  },
  mgso4: {
    id: 'mgso4',
    name: 'Magnesium Sulfate',
    formula: 'MgSO4',
    molarMass: 120.37,
    color: '#FFFFFF',
    elements: [
      { symbol: 'Mg', atomicMass: 24.31, count: 1 },
      { symbol: 'S', atomicMass: 32.07, count: 1 },
      { symbol: 'O', atomicMass: 16.00, count: 4 },
    ],
  },
  licl: {
    id: 'licl',
    name: 'Lithium Chloride',
    formula: 'LiCl',
    molarMass: 42.39,
    color: '#FFFFFF',
    elements: [
      { symbol: 'Li', atomicMass: 6.94, count: 1 },
      { symbol: 'Cl', atomicMass: 35.45, count: 1 },
    ],
  },
  na2co3: {
    id: 'na2co3',
    name: 'Sodium Carbonate',
    formula: 'Na2CO3',
    molarMass: 105.99,
    color: '#FFFFFF',
    elements: [
      { symbol: 'Na', atomicMass: 22.99, count: 2 },
      { symbol: 'C', atomicMass: 12.01, count: 1 },
      { symbol: 'O', atomicMass: 16.00, count: 3 },
    ],
  },
};

// Solution preparation targets
export interface SolutionTarget {
  number: number;
  volume: number;      // mL
  molarity: number;    // M
  description: string;
}

export const solutionTargets: SolutionTarget[] = [
  { number: 1, volume: 100, molarity: 1.00, description: '100 mL of 1.00 M solution' },
  { number: 2, volume: 50, molarity: 0.10, description: '50 mL of 0.10 M solution' },
  { number: 3, volume: 250, molarity: 0.0500, description: '250 mL of 0.0500 M solution' },
  { number: 4, volume: 100, molarity: 0.15, description: '100 mL of 0.15 M solution' },
];

// Part 1: Introduction and Compound Selection
const part1Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Introduction',
    shortDescription: 'Learn about molar mass and solution preparation',
    handler: 'handleIntroduction',
    infoContent: {
      title: 'What is Molar Mass?',
      content: `Molar mass is the mass of one mole of a substance. It is numerically equal to the mass of Avogadro's number (6.02 x 10^23) of atoms, molecules, or formula units. The unit is grams per mole (g/mol).

For example, the molar mass of CaCO3 is:
Ca(40.08) + C(12.01) + O(16.00 x 3) = 100.09 g/mol`,
      whatHappens: 'The concept of molar mass is introduced as the bridge between atomic-scale quantities (moles) and measurable laboratory masses (grams).',
      observe: [
        'Review the periodic table for atomic masses of common elements',
        'Note that molar mass units are grams per mole (g/mol)',
        'Practice calculating molar mass by summing atomic mass contributions',
      ],
    },
  },
  {
    id: 2,
    title: 'Select First Compound',
    shortDescription: 'Choose your first compound from the available options',
    handler: 'handleSelectCompound1',
    validation: {
      requires: ['introductionComplete'],
    },
  },
  {
    id: 3,
    title: 'Select Second Compound',
    shortDescription: 'Choose your second compound',
    handler: 'handleSelectCompound2',
    validation: {
      requires: ['compound1Selected'],
    },
  },
  {
    id: 4,
    title: 'Select Third Compound',
    shortDescription: 'Choose your third compound',
    handler: 'handleSelectCompound3',
    validation: {
      requires: ['compound2Selected'],
    },
  },
  {
    id: 5,
    title: 'Select Fourth Compound',
    shortDescription: 'Choose your fourth compound',
    handler: 'handleSelectCompound4',
    validation: {
      requires: ['compound3Selected'],
    },
  },
];

// Part 2: Molar Mass Calculation
const part2Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Calculate Molar Mass of Compound 1',
    shortDescription: 'Use atomic masses to calculate the molar mass',
    handler: 'handleCalculateMolarMass1',
    infoContent: {
      title: 'Calculating Molar Mass',
      content: `To calculate molar mass:
1. Identify each element in the formula
2. Multiply each element's atomic mass by its subscript
3. Add all the products together

Example: H2O
H: 1.01 g/mol x 2 = 2.02 g/mol
O: 16.00 g/mol x 1 = 16.00 g/mol
Total: 18.02 g/mol`,
      whatHappens: 'Each element in the compound formula is identified, its atomic mass is multiplied by the number of atoms, and all contributions are summed to obtain the molar mass.',
      observe: [
        'Write down the chemical formula and identify each element',
        'Look up atomic masses from the periodic table to appropriate decimal places',
        'Show your calculation step by step for the lab report',
      ],
    },
  },
  {
    id: 2,
    title: 'Calculate Molar Mass of Compound 2',
    shortDescription: 'Calculate the molar mass of your second compound',
    handler: 'handleCalculateMolarMass2',
    validation: {
      requires: ['molarMass1Calculated'],
    },
  },
  {
    id: 3,
    title: 'Calculate Molar Mass of Compound 3',
    shortDescription: 'Calculate the molar mass of your third compound',
    handler: 'handleCalculateMolarMass3',
    validation: {
      requires: ['molarMass2Calculated'],
    },
  },
  {
    id: 4,
    title: 'Calculate Molar Mass of Compound 4',
    shortDescription: 'Calculate the molar mass of your fourth compound',
    handler: 'handleCalculateMolarMass4',
    validation: {
      requires: ['molarMass3Calculated'],
    },
  },
];

// Part 3: Weighing 0.10 Mole of Each Compound
const part3Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Calculate Target Mass',
    shortDescription: 'Calculate the mass of 0.10 mole for each compound',
    handler: 'handleCalculateTargetMass',
    infoContent: {
      title: 'Mass from Moles',
      content: `To find the mass needed for a specific number of moles:

Mass (g) = Moles x Molar Mass (g/mol)

For 0.10 mol of CaCO3:
Mass = 0.10 mol x 100.09 g/mol = 10.009 g`,
      whatHappens: 'The target mass for 0.10 mole of each compound is calculated by multiplying the molar mass by 0.10 mol.',
      observe: [
        'Calculate the target mass for each of your four selected compounds',
        'Record calculations clearly for your lab report',
        'Note that different compounds will require different masses for the same number of moles',
      ],
    },
  },
  {
    id: 2,
    title: 'Tare the Balance',
    shortDescription: 'Place weigh boat and tare the analytical balance',
    handler: 'handleTareBalance',
    validation: {
      requires: ['targetMassCalculated'],
    },
    infoContent: {
      title: 'Using the Analytical Balance',
      content: `Always tare the balance with the empty container before adding your sample. **Weigh to the nearest 0.01 g.** Record all calculations and measurements and include them in the lab report. Clean up any spills immediately to maintain accuracy.

Note: When you are weighing a substance you must record the weight directly into your lab notebook (i.e. you must bring your lab notebook into the balance room with you).

Tips:
- Never place chemicals directly on the pan
- Close doors when reading
- Keep balance level`,
      whatHappens: 'The analytical balance is zeroed with an empty weigh boat so that only the mass of the added compound is measured.',
      observe: [
        'The balance display reads 0.00 g after taring with the weigh boat',
        'Balance doors should be closed when taking readings for accuracy',
        'Record the mass reading directly into your lab notebook at the balance',
      ],
    },
  },
  {
    id: 3,
    title: 'Weigh Compound 1',
    shortDescription: 'Weigh 0.10 mol of your first compound',
    handler: 'handleWeighCompound1',
    validation: {
      requires: ['balanceTared'],
    },
  },
  {
    id: 4,
    title: 'Record Mass and Reset',
    shortDescription: 'Record the mass and prepare for next compound',
    handler: 'handleRecordMass1',
    validation: {
      requires: ['compound1Weighed'],
    },
  },
  {
    id: 5,
    title: 'Weigh Compound 2',
    shortDescription: 'Weigh 0.10 mol of your second compound',
    handler: 'handleWeighCompound2',
    validation: {
      requires: ['mass1Recorded'],
    },
  },
  {
    id: 6,
    title: 'Record Mass and Reset',
    shortDescription: 'Record the mass and prepare for next compound',
    handler: 'handleRecordMass2',
    validation: {
      requires: ['compound2Weighed'],
    },
  },
  {
    id: 7,
    title: 'Weigh Compound 3',
    shortDescription: 'Weigh 0.10 mol of your third compound',
    handler: 'handleWeighCompound3',
    validation: {
      requires: ['mass2Recorded'],
    },
  },
  {
    id: 8,
    title: 'Record Mass and Reset',
    shortDescription: 'Record the mass and prepare for next compound',
    handler: 'handleRecordMass3',
    validation: {
      requires: ['compound3Weighed'],
    },
  },
  {
    id: 9,
    title: 'Weigh Compound 4',
    shortDescription: 'Weigh 0.10 mol of your fourth compound',
    handler: 'handleWeighCompound4',
    validation: {
      requires: ['mass3Recorded'],
    },
  },
  {
    id: 10,
    title: 'Record Final Mass',
    shortDescription: 'Record the mass of the fourth compound',
    handler: 'handleRecordMass4',
    validation: {
      requires: ['compound4Weighed'],
    },
  },
];

// Part 4-7: Solution Preparation (each is similar)
const createSolutionPrepPart = (solutionNumber: number, target: SolutionTarget): ExperimentStep[] => [
  {
    id: 1,
    title: 'Calculate Required Mass',
    shortDescription: `Calculate mass needed for ${target.description}`,
    handler: `handleCalculateSolutionMass${solutionNumber}`,
    infoContent: {
      title: 'Calculating Molarity',
      content: `Molarity (M) = moles of solute / liters of solution

To prepare a solution:
1. Calculate moles needed: M x V(in L)
2. Calculate mass needed: moles x molar mass
3. Weigh the calculated mass
4. Dissolve and dilute to volume

Formula: Mass (g) = Molarity x Volume (L) x Molar Mass (g/mol)`,
      whatHappens: 'The mass of solute needed is calculated from the target molarity, solution volume, and molar mass using the formula: Mass = M x V(L) x MM.',
      observe: [
        'Calculate moles needed first: Molarity x Volume in liters',
        'Then convert moles to grams: moles x molar mass',
        'Double-check calculations before weighing to avoid wasting reagent',
      ],
    },
  },
  {
    id: 2,
    title: 'Weigh Compound',
    shortDescription: 'Weigh the calculated mass into a beaker',
    handler: `handleWeighForSolution${solutionNumber}`,
    validation: {
      requires: [`solutionMass${solutionNumber}Calculated`],
    },
  },
  {
    id: 3,
    title: 'Dissolve in Beaker',
    shortDescription: 'Add deionized water to dissolve the compound',
    handler: `handleDissolve${solutionNumber}`,
    validation: {
      requires: [`solution${solutionNumber}Weighed`],
    },
  },
  {
    id: 4,
    title: 'Quantitative Transfer',
    shortDescription: 'Transfer solution to volumetric flask with rinses',
    handler: `handleTransfer${solutionNumber}`,
    validation: {
      requires: [`compound${solutionNumber}Dissolved`],
    },
    infoContent: {
      title: 'Quantitative Transfer Technique',
      content: `**Important:** You must use the solution making skills learned in Analytical Techniques. This means washing powders off of weighboats (quantitative transfer), dissolving your solute in a beaker using **LESS solvent** than the capacity of your volumetric flask, and rinsing your beaker into the volumetric flask.

Quantitative transfer ensures all of your solute is moved from the beaker to the volumetric flask:
1. Dissolve the solid completely in the beaker (using less water than flask volume)
2. Carefully pour the solution into the flask
3. Rinse the beaker with small amounts of deionised water
4. Add all rinses to the flask
5. Fill the flask up to the mark with deionised water and mix

This ensures accurate molarity calculations.`,
      whatHappens: 'The dissolved solution is carefully transferred from the beaker to the volumetric flask with multiple rinses to ensure every bit of solute is included.',
      observe: [
        'Pour the solution carefully into the volumetric flask using a funnel if needed',
        'Rinse the beaker 2-3 times with small portions of deionized water',
        'Add all rinse water to the flask to ensure quantitative (complete) transfer',
      ],
    },
  },
  {
    id: 5,
    title: 'Fill to Mark',
    shortDescription: `Fill volumetric flask to ${target.volume} mL mark`,
    handler: `handleFillToMark${solutionNumber}`,
    validation: {
      requires: [`solution${solutionNumber}Transferred`],
    },
    infoContent: {
      title: 'Volumetric Flask Precision',
      content: `The volumetric flask is calibrated to contain a precise volume at the etched line. Fill until the bottom of the meniscus touches the line. Always view at eye level for accuracy.

Warning: Do not use for heating or mixing.`,
      whatHappens: 'Deionized water is added to the volumetric flask until the bottom of the meniscus aligns exactly with the calibration mark, ensuring precise volume.',
      observe: [
        'View the meniscus at eye level to avoid parallax error',
        'The bottom of the meniscus should just touch the etched line',
        'Use a wash bottle for fine control when approaching the mark',
      ],
    },
  },
  {
    id: 6,
    title: 'Mix Solution',
    shortDescription: 'Invert flask several times to mix thoroughly',
    handler: `handleMixSolution${solutionNumber}`,
    validation: {
      requires: [`solution${solutionNumber}Filled`],
    },
  },
];

// Part 8: Results Summary
const part8Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Review Data Table',
    shortDescription: 'Review all measurements and calculations',
    handler: 'handleReviewData',
    infoContent: {
      title: 'Data Review',
      content: `Before completing the lab, verify all entries in your data table:
- Molar masses calculated correctly
- Masses weighed to 0.01 g precision
- Solutions prepared with correct molarity
- All calculations shown`,
      whatHappens: 'All experimental data is reviewed for completeness and accuracy, including molar mass calculations, recorded masses, and solution preparation details.',
      observe: [
        'Verify all molar mass calculations match expected values from the periodic table',
        'Confirm all masses were recorded to 0.01 g precision',
        'Check that solution volumes and concentrations are correctly documented',
      ],
    },
  },
  {
    id: 2,
    title: 'Verify Calculations',
    shortDescription: 'Check that all calculations are correct',
    handler: 'handleVerifyCalculations',
    validation: {
      requires: ['dataReviewed'],
    },
  },
  {
    id: 3,
    title: 'Submit Results',
    shortDescription: 'Submit your completed experiment data',
    handler: 'handleSubmitResults',
    validation: {
      requires: ['calculationsVerified'],
    },
  },
];

// Initial States
const part1InitialState = {
  introductionComplete: false,
  selectedCompounds: [] as string[],
  compound1Selected: false,
  compound2Selected: false,
  compound3Selected: false,
  compound4Selected: false,
};

const part2InitialState = {
  molarMasses: {} as Record<string, number>,
  molarMass1Calculated: false,
  molarMass2Calculated: false,
  molarMass3Calculated: false,
  molarMass4Calculated: false,
};

const part3InitialState = {
  targetMassCalculated: false,
  balanceTared: false,
  weighedMasses: {} as Record<string, number>,
  compound1Weighed: false,
  mass1Recorded: false,
  compound2Weighed: false,
  mass2Recorded: false,
  compound3Weighed: false,
  mass3Recorded: false,
  compound4Weighed: false,
  mass4Recorded: false,
  currentMass: 0,
};

const createSolutionInitialState = (num: number) => ({
  [`solutionMass${num}Calculated`]: false,
  [`solution${num}Weighed`]: false,
  [`compound${num}Dissolved`]: false,
  [`solution${num}Transferred`]: false,
  [`solution${num}Filled`]: false,
  [`solution${num}Mixed`]: false,
  calculatedMass: 0,
  actualMass: 0,
});

const part8InitialState = {
  dataReviewed: false,
  calculationsVerified: false,
  resultsSubmitted: false,
};

// Part Definitions
const part1: ExperimentPart = {
  id: 1,
  name: 'selection',
  title: 'Part I: Compound Selection',
  steps: part1Steps,
  equipment: ['Compound bottles (8 options)', 'Periodic table reference'],
  initialState: part1InitialState,
};

const part2: ExperimentPart = {
  id: 2,
  name: 'molarMass',
  title: 'Part II: Molar Mass Calculation',
  steps: part2Steps,
  equipment: ['Calculator', 'Periodic table', 'Calculation worksheet'],
  initialState: part2InitialState,
};

const part3: ExperimentPart = {
  id: 3,
  name: 'weighing',
  title: 'Part III: Weighing 0.10 Mole',
  steps: part3Steps,
  equipment: ['Analytical balance', 'Weigh boats', 'Scoopula', 'Selected compounds'],
  initialState: part3InitialState,
};

const part4: ExperimentPart = {
  id: 4,
  name: 'solution1',
  title: 'Part IV: Solution 1 (100 mL of 1.00 M)',
  steps: createSolutionPrepPart(1, solutionTargets[0]),
  equipment: ['100 mL volumetric flask', 'Beaker', 'Wash bottle', 'Stirring rod'],
  initialState: createSolutionInitialState(1),
};

const part5: ExperimentPart = {
  id: 5,
  name: 'solution2',
  title: 'Part V: Solution 2 (50 mL of 0.10 M)',
  steps: createSolutionPrepPart(2, solutionTargets[1]),
  equipment: ['50 mL volumetric flask', 'Beaker', 'Wash bottle', 'Stirring rod'],
  initialState: createSolutionInitialState(2),
};

const part6: ExperimentPart = {
  id: 6,
  name: 'solution3',
  title: 'Part VI: Solution 3 (250 mL of 0.0500 M)',
  steps: createSolutionPrepPart(3, solutionTargets[2]),
  equipment: ['250 mL volumetric flask', 'Beaker', 'Wash bottle', 'Stirring rod'],
  initialState: createSolutionInitialState(3),
};

const part7: ExperimentPart = {
  id: 7,
  name: 'solution4',
  title: 'Part VII: Solution 4 (100 mL of 0.15 M)',
  steps: createSolutionPrepPart(4, solutionTargets[3]),
  equipment: ['100 mL volumetric flask', 'Beaker', 'Wash bottle', 'Stirring rod'],
  initialState: createSolutionInitialState(4),
};

const part8: ExperimentPart = {
  id: 8,
  name: 'results',
  title: 'Part VIII: Results Summary',
  steps: part8Steps,
  equipment: ['Data table', 'Calculator'],
  initialState: part8InitialState,
};

// ============================================================================
// FULL EXPERIMENT CONFIGURATION
// ============================================================================

const experiment4Config: ExperimentConfig = {
  id: 'exp4',
  number: 4,
  name: 'Molar Mass of Compounds',
  description: 'Calculate molar mass, weigh precise amounts of compounds, and prepare solutions of known concentrations using volumetric techniques.',
  estimatedTime: '120 minutes',
  learningObjectives: [
    'Calculate molar mass from chemical formulas using atomic masses',
    'Understand the relationship between mass, moles, and molar mass',
    'Use an analytical balance with proper technique',
    'Calculate the mass needed to prepare solutions of specific molarity',
    'Perform quantitative transfer to ensure accuracy',
    'Prepare solutions using volumetric glassware',
    'Understand the precision required in quantitative chemistry',
  ],
  safetyNotes: [
    'Wear safety goggles when handling chemicals',
    'Some compounds may be irritants - avoid skin contact',
    'Clean up any spilled material in or near the balance(s) IMMEDIATELY — failure to do so will result in loss of marks',
    'Handle material with a clean spatula only',
    'Only remove material from the bottle: never return anything to the bottle',
    'Use the solution making skills learned in Analytical Techniques (quantitative transfer)',
    'Dissolve solute in a beaker using less solvent than the capacity of your volumetric flask',
    'Rinse your beaker into the volumetric flask',
    'Do NOT use volumetric flasks for heating or mixing',
    'Do not ingest any chemicals',
    'Wash hands after the experiment',
  ],
  requiredEquipment: [
    'Analytical balance',
    'Weigh boats',
    'Scoopula',
    'Compound bottles (NaCl, CuSO₄, CaCO₃, KNO₃, NaOH, MgSO₄, LiCl, Na₂CO₃)',
    'Beakers (various sizes)',
    'Volumetric flasks (50 mL, 100 mL, 250 mL)',
    'Wash bottle (deionized water)',
    'Stirring rod',
    'Calculator',
    'Periodic table',
  ],
  parts: [part1, part2, part3, part4, part5, part6, part7, part8],
  quiz: {
    experimentId: 'exp4',
    title: 'Molar Mass of Compounds Quiz',
    questions: [
      {
        id: 'exp4-q1',
        type: 'mcq' as const,
        question: 'What is the molar mass of CuSO₄ given atomic masses: Cu = 63.55, S = 32.07, O = 16.00 g/mol?',
        options: [
          { id: 'a', text: '111.62 g/mol' },
          { id: 'b', text: '159.62 g/mol' },
          { id: 'c', text: '223.17 g/mol' },
          { id: 'd', text: '95.62 g/mol' },
        ],
        correctOptionId: 'b',
        explanation: 'CuSO₄ = Cu + S + 4O = 63.55 + 32.07 + 4(16.00) = 63.55 + 32.07 + 64.00 = 159.62 g/mol.',
      },
      {
        id: 'exp4-q2',
        type: 'mcq' as const,
        question: 'To prepare 250 mL of a 0.100 M NaCl solution (molar mass = 58.44 g/mol), what mass of NaCl is needed?',
        options: [
          { id: 'a', text: '0.584 g' },
          { id: 'b', text: '1.461 g' },
          { id: 'c', text: '5.844 g' },
          { id: 'd', text: '14.61 g' },
        ],
        correctOptionId: 'b',
        explanation: 'mass = molarity × volume × molar mass = 0.100 mol/L × 0.250 L × 58.44 g/mol = 1.461 g.',
      },
      {
        id: 'exp4-q3',
        type: 'short_answer' as const,
        question: 'Explain why a quantitative transfer technique is essential when preparing solutions of known molarity using a volumetric flask.',
        sampleAnswer: 'Quantitative transfer ensures that all of the weighed solute is transferred from the weighing vessel to the volumetric flask. If any solute is left behind on the beaker or weighing boat, the actual concentration of the solution will be less than calculated. The technique involves dissolving the solute in a beaker with a small amount of solvent, then rinsing the beaker multiple times into the volumetric flask to ensure complete transfer before filling to the mark.',
      },
      {
        id: 'exp4-q4',
        type: 'calculation' as const,
        question: 'Calculate the molar mass of Na₂CO₃ given atomic masses: Na = 22.99, C = 12.01, O = 16.00 g/mol.',
        expectedValue: 105.99,
        tolerance: 1,
        unit: 'g/mol',
      },
      {
        id: 'exp4-q5',
        type: 'mcq' as const,
        question: 'Which of the following is NOT a proper technique when using an analytical balance?',
        options: [
          { id: 'a', text: 'Closing the balance doors before reading the mass' },
          { id: 'b', text: 'Using a weigh boat or paper to hold the sample' },
          { id: 'c', text: 'Returning unused chemical to the reagent bottle to avoid waste' },
          { id: 'd', text: 'Cleaning up any spilled material near the balance immediately' },
        ],
        correctOptionId: 'c',
        explanation: 'You should never return unused chemicals to the reagent bottle, as this risks contaminating the stock solution. Excess chemicals should be disposed of as directed by the instructor.',
      },
    ],
  },
};

export default experiment4Config;
